import type { CatalogPublication, ContactMessage, Notice } from "@agentry/api-client";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { useApi } from "../../app/ApiProvider";
import { useScope } from "../../app/AuthProvider";
import { useAction, useAsync } from "../../app/useAsync";
import {
	Badge,
	Button,
	Callout,
	Card,
	DefinitionList,
	Field,
	LinkButton,
	Metric,
	PageHeader,
	Select,
	StatusBadge,
	Table,
	TextArea,
	TextInput,
	Toggle,
} from "../../ui/primitives";
import {
	EmptyState,
	ErrorState,
	isNotFound,
	LoadingState,
	NotFoundState,
	recoveryFor,
} from "../../ui/states";
import { formatDateTime } from "../end-user/ProgressPages";

function ActionError({ error }: { error: unknown }) {
	if (!error) return null;
	const recovery = recoveryFor(error);
	return (
		<div
			role="alert"
			className="mt-3 rounded-lg border border-danger-500/40 bg-danger-100 px-3 py-2"
		>
			<p className="text-[13px] font-bold text-danger-600">{recovery.title}</p>
			<p className="text-[12.5px] text-ink-700">{recovery.hint}</p>
		</div>
	);
}

/** Zerovia基盤管理はAgentryの責務外であることを明示する。 */
function ScopeNote() {
	return (
		<Callout tone="neutral" title="この画面で扱わないもの">
			Provider Credential本文、Runtime内部、DB操作、基盤の障害復旧はAgentryの責務ではありません。
			Zerovia Internal Platform Adminで扱います。
		</Callout>
	);
}

export function ProductDashboardPage() {
	const { client } = useApi();
	const data = useAsync(async () => {
		const [analytics, tenants, contacts] = await Promise.all([
			client.getProductAnalytics(),
			client.listTenants(),
			client.listContactMessages(),
		]);
		return { analytics, tenants, contacts };
	}, [client]);

	return (
		<>
			<PageHeader
				eyebrow="製品管理"
				title="Agentry 製品ダッシュボード"
				description="テナント、利用、公開カタログ、問い合わせの状況です。"
			/>
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" && (
				<div className="space-y-5">
					<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
						<Metric label="テナント" value={data.data.analytics.tenants} unit="社" />
						<Metric label="有効ユーザー" value={data.data.analytics.activeUsers} unit="人" />
						<Metric label="実行" value={data.data.analytics.executions} unit="件" />
						<Metric label="月次売上" value={`¥${data.data.analytics.revenue.toLocaleString()}`} />
					</div>

					<div className="grid gap-4 lg:grid-cols-2">
						<Card
							title="テナント"
							action={
								<Link
									to="/system/tenants"
									className="text-[13px] font-semibold text-brand-600 hover:underline"
								>
									一覧
								</Link>
							}
						>
							<Table head={["テナント", "プラン", "人数", "月次"]}>
								{data.data.tenants.map((tenant) => (
									<tr key={tenant.id}>
										<td>
											<Link
												to={`/system/tenants/${tenant.id}`}
												className="font-semibold text-ink-900 hover:text-brand-600"
											>
												{tenant.name}
											</Link>
										</td>
										<td className="text-ink-600">{tenant.planId}</td>
										<td className="text-ink-600">{tenant.memberCount}</td>
										<td className="text-ink-600">¥{tenant.monthlyCost.toLocaleString()}</td>
									</tr>
								))}
							</Table>
						</Card>

						<Card
							title="未対応の問い合わせ"
							action={
								<Link
									to="/system/contacts"
									className="text-[13px] font-semibold text-brand-600 hover:underline"
								>
									一覧
								</Link>
							}
						>
							{data.data.contacts.filter((c) => c.status === "NEW").length === 0 ? (
								<EmptyState title="未対応の問い合わせはありません" />
							) : (
								<ul className="divide-y divide-ink-100">
									{data.data.contacts
										.filter((c) => c.status === "NEW")
										.map((contact) => (
											<li key={contact.id} className="py-2 first:pt-0 last:pb-0">
												<p className="text-[14px] font-semibold text-ink-900">{contact.subject}</p>
												<p className="text-[12.5px] text-ink-500">
													{contact.company} · {formatDateTime(contact.createdAt)}
												</p>
											</li>
										))}
								</ul>
							)}
						</Card>
					</div>

					<ScopeNote />
				</div>
			)}
		</>
	);
}

export function TenantsPage() {
	const { client } = useApi();
	const data = useAsync(() => client.listTenants(), [client]);

	return (
		<>
			<PageHeader eyebrow="製品管理" title="テナント" description="契約中の組織一覧です。" />
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" && (
				<Card>
					<Table head={["テナント", "識別子", "状態", "プラン", "人数", "月次費用", "契約開始"]}>
						{data.data.map((tenant) => (
							<tr key={tenant.id}>
								<td>
									<Link
										to={`/system/tenants/${tenant.id}`}
										className="font-semibold text-ink-900 hover:text-brand-600"
									>
										{tenant.name}
									</Link>
								</td>
								<td className="font-mono text-[12px] text-ink-500">{tenant.slug}</td>
								<td>
									<StatusBadge status={tenant.status} />
								</td>
								<td className="text-ink-600">{tenant.planId}</td>
								<td className="text-ink-600">{tenant.memberCount}</td>
								<td className="text-ink-600">¥{tenant.monthlyCost.toLocaleString()}</td>
								<td className="text-ink-600">{formatDateTime(tenant.createdAt)}</td>
							</tr>
						))}
					</Table>
				</Card>
			)}
		</>
	);
}

export function TenantDetailPage() {
	const { tenantId = "" } = useParams();
	const { client } = useApi();
	const data = useAsync(async () => {
		const [tenant, users] = await Promise.all([
			client.getTenantSummary(tenantId),
			client.listAllUsers(),
		]);
		return { tenant, users: users.filter((u) => u.tenantId === tenantId) };
	}, [client, tenantId]);

	if (data.status === "loading") return <LoadingState />;
	if (data.status === "error") {
		if (isNotFound(data.error)) return <NotFoundState detail="このテナントは存在しません。" />;
		return <ErrorState error={data.error} onRetry={data.reload} />;
	}

	return (
		<>
			<PageHeader
				eyebrow="製品管理"
				title={data.data.tenant.name}
				description={data.data.tenant.slug}
			/>
			<div className="grid gap-4 lg:grid-cols-2">
				<Card title="契約情報">
					<DefinitionList
						columns={1}
						items={[
							{ label: "状態", value: <StatusBadge status={data.data.tenant.status} /> },
							{ label: "プラン", value: data.data.tenant.planId },
							{ label: "シート数", value: `${data.data.tenant.memberCount} 人` },
							{ label: "月次費用", value: `¥${data.data.tenant.monthlyCost.toLocaleString()}` },
							{ label: "契約開始", value: formatDateTime(data.data.tenant.createdAt) },
						]}
					/>
				</Card>
				<Card title="ユーザー">
					<Table head={["名前", "メール", "権限", "状態"]}>
						{data.data.users.map((user) => (
							<tr key={user.id}>
								<td className="text-ink-900">{user.name}</td>
								<td className="text-ink-600">{user.email}</td>
								<td className="text-ink-600">{user.role}</td>
								<td>
									<StatusBadge status={user.status} />
								</td>
							</tr>
						))}
					</Table>
				</Card>
			</div>
			<div className="mt-4">
				<ScopeNote />
			</div>
		</>
	);
}

export function AllUsersPage() {
	const { client } = useApi();
	const [query, setQuery] = useState("");
	const data = useAsync(() => client.listAllUsers(), [client]);

	return (
		<>
			<PageHeader eyebrow="製品管理" title="ユーザー" description="全テナントのユーザーです。" />
			<div className="mb-4 max-w-sm">
				<TextInput
					type="search"
					placeholder="名前・メールで絞り込む"
					value={query}
					onChange={(event) => setQuery(event.target.value)}
					aria-label="ユーザーを絞り込む"
				/>
			</div>
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" &&
				(() => {
					const keyword = query.trim().toLowerCase();
					const visible = keyword
						? data.data.filter((u) => `${u.name}${u.email}`.toLowerCase().includes(keyword))
						: data.data;
					if (visible.length === 0) return <EmptyState title="該当するユーザーがいません" />;
					return (
						<Card>
							<Table head={["名前", "メール", "テナント", "権限", "状態", "最終利用"]}>
								{visible.map((user) => (
									<tr key={user.id}>
										<td className="font-semibold text-ink-900">{user.name}</td>
										<td className="text-ink-600">{user.email}</td>
										<td className="text-ink-600">{user.tenantId}</td>
										<td className="text-ink-600">{user.role}</td>
										<td>
											<StatusBadge status={user.status} />
										</td>
										<td className="text-ink-600">{formatDateTime(user.lastActiveAt)}</td>
									</tr>
								))}
							</Table>
						</Card>
					);
				})()}
		</>
	);
}

export function PublicationsPage() {
	const { client } = useApi();
	const [reloadSeq, setReloadSeq] = useState(0);
	const data = useAsync(() => client.listPublications(), [client, reloadSeq]);
	const setStatus = useAction(async (ucsId: string, status: CatalogPublication["status"]) =>
		client.setPublicationStatus(ucsId, status),
	);

	return (
		<>
			<PageHeader
				eyebrow="製品管理"
				title="公開Agent / UCS / Vertical"
				description="公開を停止すると、各テナントでの有効化も解除されます。"
			/>
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" && (
				<Card>
					<Table head={["UCS", "業種", "状態", "公開日", "公開範囲", "操作"]}>
						{data.data.map((publication) => (
							<tr key={publication.ucsId}>
								<td>
									<p className="font-semibold text-ink-900">{publication.displayName}</p>
									<p className="font-mono text-[11.5px] text-ink-400">{publication.ucsId}</p>
								</td>
								<td className="text-ink-600">{publication.verticalKey ?? "汎用"}</td>
								<td>
									<StatusBadge status={publication.status} />
								</td>
								<td className="text-ink-600">{formatDateTime(publication.publishedAt)}</td>
								<td className="text-ink-600">
									{publication.restrictedToTenantIds.length === 0
										? "すべてのテナント"
										: publication.restrictedToTenantIds.join(", ")}
								</td>
								<td>
									<Select
										value={publication.status}
										disabled={setStatus.pending}
										aria-label={`${publication.displayName} の公開状態`}
										className="w-36"
										onChange={async (event) => {
											await setStatus.run(
												publication.ucsId,
												event.target.value as CatalogPublication["status"],
											);
											setReloadSeq((n) => n + 1);
										}}
									>
										<option value="DRAFT">下書き</option>
										<option value="PUBLISHED">公開</option>
										<option value="DEPRECATED">提供終了</option>
									</Select>
								</td>
							</tr>
						))}
					</Table>
					<ActionError error={setStatus.error} />
				</Card>
			)}
		</>
	);
}

export function AgentBuilderPage() {
	const { client } = useApi();
	const scope = useScope();
	const data = useAsync(() => client.getCatalog(scope), [client, scope.tenantId, scope.projectId]);

	return (
		<>
			<PageHeader
				eyebrow="製品管理"
				title="Agent Builder"
				description="Common / Vertical / Custom の3層と、それを束ねるVertical Packageを管理します。"
			/>
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" && (
				<div className="space-y-4">
					<Card title="Agent">
						<Table head={["Agent", "レイヤー", "業種", "版", "状態", ""]}>
							{data.data.agents.map((agent) => (
								<tr key={agent.id}>
									<td>
										<p className="font-semibold text-ink-900">{agent.displayName}</p>
										<p className="font-mono text-[11.5px] text-ink-400">{agent.key}</p>
									</td>
									<td>
										<Badge
											tone={
												agent.layer === "COMMON"
													? "neutral"
													: agent.layer === "VERTICAL"
														? "brand"
														: "success"
											}
										>
											{agent.layer}
										</Badge>
									</td>
									<td className="text-ink-600">{agent.verticalKey ?? "—"}</td>
									<td className="text-ink-600">v{agent.version}</td>
									<td>
										<StatusBadge status={agent.status} />
									</td>
									<td>
										<LinkButton to={`/system/agents/${agent.id}`} variant="secondary" size="sm">
											編集
										</LinkButton>
									</td>
								</tr>
							))}
						</Table>
					</Card>

					<Card title="Vertical Package" description="業種ごとの知識・質問・品質チェック・KPIの束">
						<div className="grid gap-3 lg:grid-cols-2">
							{data.data.verticalPackages.map((pack) => (
								<div key={pack.id} className="rounded-lg border border-ink-200 px-3 py-2.5">
									<p className="text-[14.5px] font-semibold text-ink-900">
										{pack.displayName}{" "}
										<span className="text-[12px] text-ink-500">v{pack.version}</span>
									</p>
									<ul className="mt-1.5 space-y-0.5 text-[12.5px] text-ink-600">
										<li>知識 {pack.industryKnowledge.length} 件</li>
										<li>質問 {pack.guidedQuestions.length} 件</li>
										<li>品質チェック {pack.qualityGates.length} 件</li>
										<li>KPI {pack.kpis.length} 件</li>
									</ul>
									<p className="mt-1.5 font-mono text-[11px] text-ink-400">{pack.contentHash}</p>
								</div>
							))}
						</div>
					</Card>

					<Card
						title="Workflow定義"
						description="公開済み定義は変更できません（新しい版を作ります）"
					>
						<Table head={["Workflow", "版", "状態", "工程数", "Hash"]}>
							{data.data.workflowDefinitions.map((workflow) => (
								<tr key={workflow.id}>
									<td className="font-semibold text-ink-900">{workflow.displayName}</td>
									<td className="text-ink-600">v{workflow.version}</td>
									<td>
										<StatusBadge status={workflow.status} />
									</td>
									<td className="text-ink-600">{workflow.nodes.length}</td>
									<td className="font-mono text-[11px] text-ink-400">{workflow.definitionHash}</td>
								</tr>
							))}
						</Table>
					</Card>
				</div>
			)}
		</>
	);
}

export function AgentEditPage() {
	const { agentId = "" } = useParams();
	const { client } = useApi();
	const scope = useScope();
	const data = useAsync(() => client.getCatalog(scope), [client, scope.tenantId, scope.projectId]);

	if (data.status === "loading") return <LoadingState />;
	if (data.status === "error") return <ErrorState error={data.error} onRetry={data.reload} />;

	const agent = data.data.agents.find((a) => a.id === agentId);
	if (!agent) return <NotFoundState detail="このAgentは存在しません。" />;
	const parent = data.data.agents.find((a) => a.id === agent.parentAgentId);
	const pack = data.data.verticalPackages.find((p) => p.key === agent.verticalKey);

	return (
		<>
			<PageHeader
				eyebrow="Agent Builder"
				title={agent.displayName}
				description={agent.description}
				actions={
					<LinkButton to="/system/agents" variant="secondary">
						一覧へ戻る
					</LinkButton>
				}
			/>

			<Callout tone="warning" title="POCでは編集を保存できません">
				Agent定義の更新Endpointが未実装のため、この画面は現在の定義の参照のみです。
				保存できないものを保存できるように見せません。
			</Callout>

			<div className="mt-4 grid gap-4 lg:grid-cols-2">
				<Card title="定義">
					<DefinitionList
						columns={1}
						items={[
							{ label: "キー", value: agent.key, mono: true },
							{ label: "レイヤー", value: agent.layer },
							{ label: "継承元", value: parent?.displayName ?? "なし（最上位）" },
							{ label: "業種", value: agent.verticalKey ?? "汎用" },
							{ label: "既定のTask Type", value: agent.defaultTaskType },
							{ label: "版", value: `v${agent.version}` },
							{ label: "状態", value: <StatusBadge status={agent.status} /> },
						]}
					/>
				</Card>

				{pack && (
					<>
						<Card title="Guided Question">
							<ol className="ml-4 list-decimal space-y-1 text-[13.5px] text-ink-700">
								{pack.guidedQuestions.map((q) => (
									<li key={q.key}>
										{q.prompt}
										<span className="ml-1 text-[11.5px] text-ink-400">({q.inputType})</span>
									</li>
								))}
							</ol>
						</Card>
						<Card title="Quality Gate" description="下位レイヤーから弱められません">
							<Table head={["チェック", "種別", "判定", "閾値"]}>
								{pack.qualityGates.map((gate) => (
									<tr key={gate.key}>
										<td className="text-ink-900">{gate.displayName}</td>
										<td className="text-ink-600">{gate.gateType}</td>
										<td className="text-ink-600">{gate.evaluator}</td>
										<td className="text-ink-600">{gate.threshold ?? "—"}</td>
									</tr>
								))}
							</Table>
						</Card>
						<Card title="KPI">
							<div className="flex flex-wrap gap-1.5">
								{pack.kpis.map((kpi) => (
									<Badge key={kpi.key} tone="neutral">
										{kpi.displayName}（{kpi.unit}）
									</Badge>
								))}
							</div>
						</Card>
					</>
				)}
			</div>
		</>
	);
}

export function PlansPage() {
	const { client } = useApi();
	const [reloadSeq, setReloadSeq] = useState(0);
	const [drafts, setDrafts] = useState<Record<string, string>>({});
	const data = useAsync(() => client.listPlans(), [client, reloadSeq]);
	const update = useAction(async (planId: string, monthlyPrice: number) =>
		client.updatePlan(planId, { monthlyPrice }),
	);

	return (
		<>
			<PageHeader
				eyebrow="製品管理"
				title="プラン / Entitlement"
				description="プランごとの提供範囲です。"
			/>
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" && (
				<Card>
					<Table head={["プラン", "区分", "月額", "実行時間", "シート", "提供UCS", ""]}>
						{data.data.map((plan) => (
							<tr key={plan.id}>
								<td className="font-semibold text-ink-900">{plan.displayName}</td>
								<td>
									<Badge tone={plan.tier === "ENTERPRISE" ? "brand" : "neutral"}>{plan.tier}</Badge>
								</td>
								<td>
									<TextInput
										type="number"
										min={0}
										className="w-32"
										value={drafts[plan.id] ?? String(plan.monthlyPrice)}
										onChange={(event) => setDrafts({ ...drafts, [plan.id]: event.target.value })}
										aria-label={`${plan.displayName} の月額`}
									/>
								</td>
								<td className="text-ink-600">{plan.runtimeHours}h</td>
								<td className="text-ink-600">{plan.seats}</td>
								<td className="text-ink-600">
									{plan.entitlements.includes("*") ? "すべて" : `${plan.entitlements.length} 件`}
								</td>
								<td>
									<Button
										size="sm"
										variant="secondary"
										disabled={update.pending}
										onClick={async () => {
											await update.run(plan.id, Number(drafts[plan.id] ?? plan.monthlyPrice));
											setReloadSeq((n) => n + 1);
										}}
									>
										保存
									</Button>
								</td>
							</tr>
						))}
					</Table>
					<ActionError error={update.error} />
				</Card>
			)}
		</>
	);
}

export function NoticesPage() {
	const { client } = useApi();
	const [reloadSeq, setReloadSeq] = useState(0);
	const [form, setForm] = useState<{
		title: string;
		body: string;
		category: Notice["category"];
		status: Notice["status"];
	}>({
		title: "",
		body: "",
		category: "RELEASE",
		status: "DRAFT",
	});
	const data = useAsync(() => client.listNotices(), [client, reloadSeq]);
	const upsert = useAction(async () => client.upsertNotice(form));

	return (
		<>
			<PageHeader
				eyebrow="製品管理"
				title="お知らせ / CMS"
				description="公開すると各テナントへ表示されます。"
			/>
			<div className="grid gap-4 lg:grid-cols-[1fr_340px]">
				<Card title="お知らせ一覧">
					{data.status === "loading" && <LoadingState />}
					{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
					{data.status === "ready" &&
						(data.data.length === 0 ? (
							<EmptyState title="お知らせはありません" />
						) : (
							<Table head={["タイトル", "区分", "状態", "対象", "公開日"]}>
								{data.data.map((notice) => (
									<tr key={notice.id}>
										<td>
											<p className="font-semibold text-ink-900">{notice.title}</p>
											<p className="line-clamp-1 text-[12.5px] text-ink-500">{notice.body}</p>
										</td>
										<td className="text-ink-600">{notice.category}</td>
										<td>
											<StatusBadge status={notice.status} />
										</td>
										<td className="text-ink-600">{notice.audience}</td>
										<td className="text-ink-600">{formatDateTime(notice.publishedAt)}</td>
									</tr>
								))}
							</Table>
						))}
				</Card>

				<Card title="新しいお知らせ">
					<form
						className="space-y-3"
						onSubmit={async (event) => {
							event.preventDefault();
							const created = await upsert.run();
							if (created) {
								setForm({ title: "", body: "", category: "RELEASE", status: "DRAFT" });
								setReloadSeq((n) => n + 1);
							}
						}}
					>
						<Field label="タイトル">
							<TextInput
								value={form.title}
								onChange={(e) => setForm({ ...form, title: e.target.value })}
								required
							/>
						</Field>
						<Field label="本文">
							<TextArea
								rows={5}
								value={form.body}
								onChange={(e) => setForm({ ...form, body: e.target.value })}
								required
							/>
						</Field>
						<Field label="区分">
							<Select
								value={form.category}
								onChange={(e) =>
									setForm({ ...form, category: e.target.value as Notice["category"] })
								}
							>
								<option value="RELEASE">リリース</option>
								<option value="MAINTENANCE">メンテナンス</option>
								<option value="INCIDENT">障害</option>
							</Select>
						</Field>
						<Toggle
							checked={form.status === "PUBLISHED"}
							onChange={(next) => setForm({ ...form, status: next ? "PUBLISHED" : "DRAFT" })}
							label="すぐに公開する"
						/>
						<Button type="submit" className="w-full" disabled={upsert.pending}>
							保存する
						</Button>
						<ActionError error={upsert.error} />
					</form>
				</Card>
			</div>
		</>
	);
}

export function ContactsPage() {
	const { client } = useApi();
	const [reloadSeq, setReloadSeq] = useState(0);
	const data = useAsync(() => client.listContactMessages(), [client, reloadSeq]);
	const update = useAction(async (id: string, status: ContactMessage["status"]) =>
		client.updateContactStatus(id, status),
	);

	return (
		<>
			<PageHeader
				eyebrow="製品管理"
				title="問い合わせ"
				description="公開サイトからの問い合わせです。"
			/>
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" &&
				(data.data.length === 0 ? (
					<EmptyState title="問い合わせはありません" />
				) : (
					<div className="space-y-3">
						{data.data.map((contact) => (
							<Card
								key={contact.id}
								title={contact.subject}
								description={`${contact.name}（${contact.company}） · ${contact.email}`}
								action={
									<Select
										value={contact.status}
										disabled={update.pending}
										aria-label={`${contact.subject} の対応状況`}
										className="w-32"
										onChange={async (event) => {
											await update.run(contact.id, event.target.value as ContactMessage["status"]);
											setReloadSeq((n) => n + 1);
										}}
									>
										<option value="NEW">未対応</option>
										<option value="IN_PROGRESS">対応中</option>
										<option value="CLOSED">完了</option>
									</Select>
								}
							>
								<p className="whitespace-pre-wrap text-[14px] text-ink-700">{contact.body}</p>
								<p className="mt-2 text-[12px] text-ink-400">{formatDateTime(contact.createdAt)}</p>
							</Card>
						))}
						<ActionError error={update.error} />
					</div>
				))}
		</>
	);
}

export function AnalyticsPage() {
	const { client } = useApi();
	const data = useAsync(() => client.getProductAnalytics(), [client]);

	return (
		<>
			<PageHeader eyebrow="製品管理" title="分析" description="実行数と売上の推移です。" />
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" && (
				<div className="space-y-4">
					<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
						<Metric label="テナント" value={data.data.tenants} unit="社" />
						<Metric label="有効ユーザー" value={data.data.activeUsers} unit="人" />
						<Metric label="実行" value={data.data.executions} unit="件" />
						<Metric label="成果物" value={data.data.artifacts} unit="件" />
					</div>

					<Card title="推移">
						{(() => {
							const max = Math.max(...data.data.series.map((s) => s.executions), 1);
							return (
								<div className="flex items-end gap-4">
									{data.data.series.map((point) => (
										<div key={point.label} className="flex flex-1 flex-col items-center gap-1">
											<span className="text-[12px] font-semibold text-ink-700">
												{point.executions}
											</span>
											<div
												className="w-full rounded-t bg-brand-500"
												style={{ height: `${Math.max((point.executions / max) * 140, 4)}px` }}
											/>
											<span className="text-[11.5px] text-ink-500">{point.label}</span>
											<span className="text-[11px] text-ink-400">
												¥{point.revenue.toLocaleString()}
											</span>
										</div>
									))}
								</div>
							);
						})()}
					</Card>
				</div>
			)}
		</>
	);
}

export function ProductSettingsPage() {
	const { client } = useApi();
	const [reloadSeq, setReloadSeq] = useState(0);
	const data = useAsync(() => client.getProductSettings(), [client, reloadSeq]);
	const update = useAction(async (input: Record<string, unknown>) =>
		client.updateProductSettings(input),
	);

	if (data.status === "loading") return <LoadingState />;
	if (data.status === "error") return <ErrorState error={data.error} onRetry={data.reload} />;

	const settings = data.data;

	return (
		<>
			<PageHeader eyebrow="製品管理" title="製品設定" />
			<div className="grid gap-4 lg:grid-cols-2">
				<Card title="基本">
					<DefinitionList
						columns={1}
						items={[
							{ label: "製品名", value: settings.productName },
							{ label: "サポート窓口", value: settings.supportEmail },
							{ label: "既定プラン", value: settings.defaultPlanId },
						]}
					/>
				</Card>
				<Card title="公開設定">
					<div className="space-y-3">
						<Toggle
							checked={settings.signupEnabled}
							disabled={update.pending}
							label="新規登録を受け付ける"
							onChange={async (next) => {
								await update.run({ signupEnabled: next });
								setReloadSeq((n) => n + 1);
							}}
						/>
						<Toggle
							checked={settings.demoEnabled}
							disabled={update.pending}
							label="公開デモを表示する"
							onChange={async (next) => {
								await update.run({ demoEnabled: next });
								setReloadSeq((n) => n + 1);
							}}
						/>
						<Toggle
							checked={settings.maintenanceMode}
							disabled={update.pending}
							label="メンテナンスモード"
							onChange={async (next) => {
								await update.run({ maintenanceMode: next });
								setReloadSeq((n) => n + 1);
							}}
						/>
					</div>
					<ActionError error={update.error} />
				</Card>
			</div>
			<div className="mt-4">
				<ScopeNote />
			</div>
		</>
	);
}
